import { useEffect, useMemo, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useCreateAssignment, useUpdateAssignment } from './useAssignmentMutations';
import type { ClassOfferingDetail, GradebookFromQuery } from './classDetailTypes';

type Assignment = NonNullable<ClassOfferingDetail['assignments']>[number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classOfferingId: string;
  gradebooks: ReadonlyArray<GradebookFromQuery>;
  assignment: Assignment | null;
  onComplete: () => void;
}

export default function AssignmentDialog({
  open, onOpenChange, classOfferingId, gradebooks, assignment, onComplete,
}: Props) {
  const editing = !!assignment;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pointsPossible, setPointsPossible] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [selectedTargets, setSelectedTargets] = useState<Record<string, string | null>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createAssignment] = useCreateAssignment();
  const [updateAssignment] = useUpdateAssignment();
  const pointsGradebooks = useMemo(
    () => gradebooks.filter(
      (gradebook) => gradebook.gradingMode === 'POINTS' && !gradebook.finalized
    ),
    [gradebooks]
  );

  useEffect(() => {
    if (assignment) {
      setName(assignment.name);
      setDescription(assignment.description ?? '');
      setPointsPossible(assignment.pointsPossible == null ? '' : String(assignment.pointsPossible));
      setDueOn(String(assignment.dueOn ?? ''));
      setSelectedTargets(Object.fromEntries(
        (assignment.targets ?? []).map((target) => [
          target.gradebook.id,
          target.gradebookCategory?.id ?? null,
        ])
      ));
    } else {
      setName('');
      setDescription('');
      setPointsPossible('');
      setDueOn('');
      setSelectedTargets({});
    }
    setError(null);
  }, [assignment, pointsGradebooks, open]);

  const toggleGradebook = (gradebook: GradebookFromQuery, checked: boolean) => {
    setSelectedTargets((current) => {
      if (!checked) {
        const next = { ...current };
        delete next[gradebook.id];
        return next;
      }

      return {
        ...current,
        [gradebook.id]: null,
      };
    });
  };

  const targetInput = () => Object.entries(selectedTargets).map(
    ([gradebookId, gradebookCategoryId]) => ({ gradebookId, gradebookCategoryId })
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Name is required.');
        setSaving(false);
        return;
      }
      if (pointsPossible.trim() === '') {
        setError('Enter the points possible. Use 0 for extra credit.');
        setSaving(false);
        return;
      }
      const pts = parseFloat(pointsPossible);
      if (isNaN(pts) || pts < 0) {
        setError('Enter the points possible. Use 0 for extra credit.');
        setSaving(false);
        return;
      }
      if (Object.keys(selectedTargets).length === 0) {
        setError('Select at least one gradebook.');
        setSaving(false);
        return;
      }
      const missingCategory = pointsGradebooks.find(
        (gradebook) =>
          Object.prototype.hasOwnProperty.call(selectedTargets, gradebook.id) &&
          gradebook.categories.length > 0 &&
          !selectedTargets[gradebook.id]
      );
      if (missingCategory) {
        setError(`Choose a category for ${missingCategory.name}.`);
        setSaving(false);
        return;
      }

      if (editing && assignment) {
        const res = await updateAssignment({
          variables: {
            input: {
              id: assignment.id,
              name: trimmed,
              description: description.trim() || null,
              pointsPossible: pts,
              targets: targetInput(),
              ...(dueOn ? { dueOn } : { clearDueOn: true }),
            },
          },
        });
        const userErrors = res.data?.updateAssignment?.userErrors;
        if (userErrors?.length) {
          setError(userErrors.map((e: any) => e.message).join(', '));
          return;
        }
      } else {
        const res = await createAssignment({
          variables: {
            input: {
              classOfferingId,
              name: trimmed,
              description: description.trim() || null,
              pointsPossible: pts,
              targets: targetInput(),
              ...(dueOn ? { dueOn } : {}),
            },
          },
        });
        const userErrors = res.data?.createAssignment?.userErrors;
        if (userErrors?.length) {
          setError(userErrors.map((e: any) => e.message).join(', '));
          return;
        }
      }
      onComplete();
    } catch (e: any) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Assignment' : 'New Assignment'}</DialogTitle>
          <DialogDescription>
            {editing ? 'Update the assignment details.' : 'Add a graded item to this class.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Chapter 4 Test" autoFocus />
          </div>
          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Input id="description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="points">Points possible</Label>
              <Input
                id="points"
                type="number"
                value={pointsPossible}
                onChange={(e) => setPointsPossible(e.target.value)}
                placeholder="Required"
                min="0"
                step="0.01"
                aria-describedby="points-help"
              />
              <p id="points-help" className="mt-1 text-xs text-muted-foreground">
                Required. Use 0 for extra credit. Earned points may exceed points possible.
              </p>
            </div>
            <div>
              <Label htmlFor="dueOn">Due date</Label>
              <Input id="dueOn" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Gradebook targets</legend>
            <p className="text-xs text-muted-foreground">
              The same student score is used in every selected gradebook.
            </p>
            {pointsGradebooks.map((gradebook) => {
              const selected = Object.prototype.hasOwnProperty.call(selectedTargets, gradebook.id);
              return (
                <div key={gradebook.id} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`gradebook-${gradebook.id}`}
                      checked={selected}
                      onCheckedChange={(checked) => toggleGradebook(gradebook, checked === true)}
                    />
                    <Label htmlFor={`gradebook-${gradebook.id}`} className="font-medium">
                      {gradebook.name}
                    </Label>
                  </div>
                  {selected && gradebook.categories.length > 0 && (
                    <div className="pl-6">
                      <Label htmlFor={`category-${gradebook.id}`}>Category for {gradebook.name}</Label>
                      <Select
                        value={selectedTargets[gradebook.id] ?? undefined}
                        onValueChange={(value) => setSelectedTargets((current) => ({
                          ...current,
                          [gradebook.id]: value,
                        }))}
                      >
                        <SelectTrigger id={`category-${gradebook.id}`}>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {gradebook.categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selected && gradebook.categories.length === 0 && (
                    <p className="pl-6 text-xs text-muted-foreground">
                      Total-points grading — no category required.
                    </p>
                  )}
                </div>
              );
            })}
          </fieldset>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Save' : 'Create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
